import { motion } from "framer-motion";
import { MapPin, BedDouble, Bus, BadgeCheck, Plane } from "lucide-react";
import SectionHead from "./SectionHead";
import { fadeUp, stagger, sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

const ITEM_ICONS = [MapPin, BedDouble, Bus, BadgeCheck];

const AIRPORTS = [
  { code: "BLQ", city: "Bologna" },
  { code: "FRL", city: "Forlì" },
  { code: "RMI", city: "Rimini" },
];

const CIRCUIT_IMG =
  "https://images.unsplash.com/photo-1564405133580-7446ed4e260d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxyYWNpbmclMjBjaXJjdWl0JTIwcG9kaXVtJTIwcGl0JTIwc3RvcCUyMG1pc2FubyUyMGl0YWx5fGVufDB8fHx8MTc5MTU1MDAwMHww&ixlib=rb-4.1.0&q=85";

export default function Logistica() {
  const { t } = useLang();
  const c = t.logistica;

  return (
    <section id="logistica" className={`relative bg-vg-card ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="06" kicker={c.kicker} title={c.title} en={c.en} />

        <div className="grid gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="clip-frame relative min-h-[280px] overflow-hidden border border-white/10 lg:col-span-5"
            data-testid="logistica-image"
          >
            <img src={CIRCUIT_IMG} alt="Paddock del circuito di Misano" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-vg-black/85 via-transparent to-transparent" aria-hidden="true" />
            <div className="absolute bottom-5 left-5 right-5">
              <p className="font-display text-2xl font-bold uppercase tracking-wide text-vg-white">{c.imgTitle}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{c.imgSub}</p>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
            {c.items.map((item, i) => (
              <motion.article key={item.title} variants={fadeUp} className="border border-white/10 bg-vg-black p-7" data-testid={`logistica-${i}`}>
                {ITEM_ICONS[i] && (() => { const Icon = ITEM_ICONS[i]; return <Icon className="h-7 w-7 text-vg-green" />; })()}
                <h3 className="mt-4 font-display text-xl font-semibold uppercase tracking-wider text-vg-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-vg-muted">{item.desc}</p>
              </motion.article>
            ))}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-between gap-4 border border-vg-green/30 bg-vg-green/5 p-7 sm:col-span-2" data-testid="navette-strip">
              <div className="flex items-center gap-4">
                <Plane className="h-7 w-7 text-vg-green" />
                <p className="font-display text-lg font-semibold uppercase tracking-wider text-vg-white">{c.stripTitle}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {AIRPORTS.map((a) => (
                  <span key={a.code} className="hud-chip">
                    {a.code} · {a.city}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
